import React, { useRef } from 'react';
import CareerPathwayDataPoints from '@/components/DesignSystem/Icons/svgs/CareerPathwayDataPoints';
import { useCalculatorContext } from '@/components/DesignSystem/Subsections/Calculator/contexts';
import DegreeLengthDataPoints from '@/components/DesignSystem/Icons/svgs/DegreeLengthDataPoints';
import SalaryDataPoints from '@/components/DesignSystem/Icons/svgs/SalaryDataPoints';
import CalculatorFormPanel from './CalculatorFormPanel';
import ThreeDotLoader from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/ThreeDotLoader';
import uuid from '@/utils/uuid';
import useViewEvents from '@/hooks/useViewEvent';
import tagularEvent from '@/utils/tagular/tagularEvent';
import { ProductViewed, ProductClicked } from '@/types/Tagular';
import { useDynamicExperienceContext } from '@/components/DynamicFlow/providers/DynamicExperienceProvider';
import { useGlobalProps } from '@/components/GlobalContextProvider';
import useFlowTrackingEvents from '@/components/DynamicFlow/hooks/useFlowTrackingEvent';

const CalculatorWidget = () => {
  const {
    state,
    config,
    triggerDataFetch,
    variant,
    isFormComplete,
    isCalculating,
    setCorrelationId,
  } = useCalculatorContext();

  // Generate viewCorrelationId using useRef to persist across renders
  const viewCorrelationIdRef = useRef(uuid());
  const viewCorrelationId = viewCorrelationIdRef.current;
  const { monarchExperience } = useGlobalProps();
  const { slotPlacement, currentStep, setClickedCtaLabel, eventContext } =
    useDynamicExperienceContext();
  const { triggerProductClickedEvent } = useFlowTrackingEvents();

  // ProductViewed event payload
  const productViewedPayload: ProductViewed = {
    viewCorrelationId,
    customDimensions: [
      { key: 'experience', value: monarchExperience?.experienceName || '' },
      { key: 'slotName', value: slotPlacement?.slotName || '' },
      { key: 'slotId', value: slotPlacement?.slotCustomId || '' },
      {
        key: 'slotVariant',
        value: slotPlacement?.value?.PersonalizedAssetType || 'default',
      },
    ],
    product: {
      brand: '',
      formatSubtype: config?.tracking.formatSubtype || '',
      formatType: 'dexp-tool-entry',
      location: config?.tracking.location || '',
    },
  };

  const ElementViewedPayload = {
    correlationId: uuid(),
    webElement: {
      elementType: 'tool',
      location: config?.tracking.location || '',
      name: config?.tracking.name || '',
      position: config?.tracking.position || '',
      text: config?.ctaText || '',
    },
  };

  // Use useViewEvents to track when the widget comes into view
  const { ref } = useViewEvents([
    {
      name: 'ProductViewed',
      payload: productViewedPayload,
    },
    {
      name: 'ElementViewed',
      payload: ElementViewedPayload,
    },
  ]);

  // Render data points component based on variant
  const renderDataPointsComponent = () => {
    switch (variant) {
      case 'degree-length':
        return <DegreeLengthDataPoints />;
      case 'career-details':
        return <CareerPathwayDataPoints />;
      case 'salary':
        return <SalaryDataPoints />;
      default:
        // Default to DegreeLengthDataPoints if variant is not specified
        return <DegreeLengthDataPoints />;
    }
  };

  const handleSubmit = () => {
    if (!isFormComplete() || !triggerDataFetch) return;

    // When this calculator is the dynamic flow entry step, set CTA label and advance so provider fires ProductClicked
    if (currentStep?.isEntry && setClickedCtaLabel) {
      setClickedCtaLabel(config?.ctaText ?? '');
      eventContext.ctaLabel = config?.ctaText ?? '';
    }

    // Standalone / non-entry: fire ProductClicked here and show results
    const correlationId = uuid();
    setCorrelationId?.(correlationId);

    if (eventContext.dynamicExperienceEventFlag) {
      triggerProductClickedEvent({
        ...eventContext,
        customDimensions: [
          ...(eventContext.customDimensions || []),
          { key: 'ctaText', value: config?.ctaText ?? '' },
        ],
      });
    } else {
      const productClickedPayload: ProductClicked = {
        correlationId,
        viewCorrelationId,
        product: {
          brand: '',
          formatSubtype: config?.tracking.formatSubtype || '',
          formatType: 'dexp-tool-entry',
          location: config?.tracking.location || '',
        },
      };

      tagularEvent('ProductClicked', productClickedPayload);
    }

    triggerDataFetch();
  };

  return (
    <div
      ref={ref as React.LegacyRef<HTMLDivElement>}
      className={`bg-gray-25 border border-gray-200 max-w-4xl mx-auto rounded-xl p-3 md:p-5 not-prose`}
    >
      {/* Top Section */}
      <div className='flex flex-row gap-8 pb-5 md:max-h-52'>
        <div className='flex flex-col w-full md:w-1/2'>
          <span className='text-xl md:text-2xl font-bold pt-2 md:pt-5 pb-2 text-black'>
            {config?.title}
          </span>
          <span className='text-base md:text-lg text-gray-500'>
            {config?.description}
          </span>
        </div>
        <div className='hidden md:flex w-1/2 object-contain'>
          {renderDataPointsComponent()}
        </div>
      </div>
      {/* Bottom Section */}
      <div className='flex flex-col gap-5'>
        <div className='italic text-base md:text-lg font-bold text-black'>
          {isCalculating ? 'Calculating your results...' : config?.formTitle}
        </div>
        {/* Dropdowns or Loading State */}
        {isCalculating ? (
          <div className='flex flex-col gap-4'>
            <ThreeDotLoader className='flex items-center justify-start py-4' />
          </div>
        ) : (
          <CalculatorFormPanel
            initiallyEditable={true}
            showSubmitButton={true}
            onSubmit={handleSubmit}
            viewCorrelationId={viewCorrelationId}
          />
        )}
      </div>
    </div>
  );
};

export default CalculatorWidget;
